import { Stack, Tooltip, Typography } from "@mui/material";
import {
  COMPETENCIES_IDS_PARAM_NAME,
  Competency_Map_Tabs_Values,
  KNOWLEDGE_BLOCKS_IDS_PARAM_NAME,
} from "constants/global";
import { getIdsSearchParam } from "helpers/index";
import useTab from "hooks/useTab";
import qs from "qs";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import palette from "theme/palette";
import { CompetencyMapWithProgressInfo } from "../types";
import { getProgressInfoCards } from "../utils";

interface ContentProps {
  competencyMapWithProgressInfo: CompetencyMapWithProgressInfo | undefined;
}

const Content = ({ competencyMapWithProgressInfo }: ContentProps) => {
  const tab = useTab<Competency_Map_Tabs_Values>();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => qs.parse(searchParams.toString()), [searchParams]) as Record<string, string>;

  const handleItemClick = ({
    competencyId,
    knowledgeBlockId,
    questionId,
  }: {
    competencyId: string;
    knowledgeBlockId?: string;
    questionId?: string | null;
  }) => {
    if (tab === Competency_Map_Tabs_Values.constructor) {
      const competentiesIdsParamValue = getIdsSearchParam(
        competencyId,
        params[COMPETENCIES_IDS_PARAM_NAME],
        false
      ).join(",");

      const knowledgeBlocksIdsParamValue = knowledgeBlockId
        ? getIdsSearchParam(knowledgeBlockId, params[KNOWLEDGE_BLOCKS_IDS_PARAM_NAME], false).join(",")
        : null;

      let paramsWithIdsParam: Record<string, string> = {
        ...params,
        [COMPETENCIES_IDS_PARAM_NAME]: competentiesIdsParamValue,
      };

      if (knowledgeBlocksIdsParamValue) {
        paramsWithIdsParam = { ...paramsWithIdsParam, [KNOWLEDGE_BLOCKS_IDS_PARAM_NAME]: knowledgeBlocksIdsParamValue };
      }

      setSearchParams(qs.stringify({ ...paramsWithIdsParam, scrollIntoViewId: competencyId }), { replace: true });
    } else if (tab === Competency_Map_Tabs_Values.questionsList) {
      setSearchParams(qs.stringify({ ...params, scrollIntoViewId: questionId }), { replace: true });
    }
  };

  const progressInfoCards = getProgressInfoCards(competencyMapWithProgressInfo?.competences);

  return (
    <Stack spacing={1}>
      {progressInfoCards.map((progressInfoCard, index) => (
        <Stack
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          sx={{ borderWidth: 1, borderStyle: "solid", borderRadius: 2, p: 1 }}>
          {progressInfoCard.map((progressInfoItem) => {
            const itemDisabled = tab === Competency_Map_Tabs_Values.questionsList && !progressInfoItem.confirmedCount;
            const { sx: rowStackSx, ...restRowStackProps } = progressInfoItem.rowStackProps || {};
            const { sx: titleSx, ...restTitleProps } = progressInfoItem.title?.titleProps || {};

            const handleClick = () =>
              handleItemClick({
                ...progressInfoItem.getIdsForSearchParams(),
                questionId: progressInfoItem.firstQuestionId,
              });

            return (
              <Stack key={progressInfoItem.id}>
                <Tooltip title={itemDisabled ? "Отсутствуют вопросы" : ""}>
                  <Stack
                    direction='row'
                    spacing={2}
                    onClick={progressInfoItem.handleClickOnLabel ? undefined : handleClick}
                    sx={{
                      opacity: itemDisabled ? 0.6 : 1,
                      ...(!progressInfoItem.handleClickOnLabel && {
                        cursor: itemDisabled ? "default" : "pointer",
                        pointerEvents: itemDisabled ? "none" : "auto",
                      }),
                      ...rowStackSx,
                    }}
                    {...restRowStackProps}>
                    <Typography
                      color={palette.base[200]}
                      sx={{
                        whiteSpace: "nowrap",
                        minWidth: 130,
                        boxSizing: "content-box",
                        textAlign: "right",
                        ...titleSx,
                      }}
                      {...restTitleProps}>
                      {progressInfoItem.title?.icon} {progressInfoItem.title?.text}
                    </Typography>
                    <Stack
                      direction='row'
                      spacing={1}
                      justifyContent='space-between'
                      onClick={progressInfoItem.handleClickOnLabel ? handleClick : undefined}
                      sx={{
                        width: "100%",
                        ...(progressInfoItem.handleClickOnLabel &&
                          !itemDisabled && { cursor: "pointer", pointerEvents: "auto" }),
                      }}>
                      <Typography>{progressInfoItem.label}</Typography>
                      <Typography
                        color={progressInfoItem.totalCount ? palette.status.received : palette.error.contrastText}
                        sx={{ wordBreak: "normal" }}>
                        {progressInfoItem.totalCount ?? 0}
                      </Typography>
                    </Stack>
                  </Stack>
                </Tooltip>
              </Stack>
            );
          })}
        </Stack>
      ))}
    </Stack>
  );
};

export default Content;
